import React, { useState } from 'react';
import { FaLock, FaSpinner, FaEye, FaEyeSlash, FaExclamationTriangle } from 'react-icons/fa';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ChangePasswordModal = ({ isOpen, onClose, isForced = false, onSuccess }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword === currentPassword) {
      setError('New password must be different from current password');
      return;
    }
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess(true);
      
      setTimeout(() => {
        onSuccess && onSuccess();
        onClose();
      }, 1500);
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="change-password-modal">
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 w-full max-w-md border border-[#2D5A8F] shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="bg-yellow-500/20 p-3 rounded-lg mr-3">
              <FaLock className="text-yellow-400 text-xl" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Change Password</h2>
              {isForced && (
                <p className="text-yellow-400 text-sm">Required for security</p>
              )}
            </div>
          </div>
          {!isForced && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
              data-testid="close-modal-btn"
            >
              ×
            </button>
          )}
        </div>

        {/* Forced Change Warning */}
        {isForced && (
          <div className="mb-4 p-3 bg-yellow-500/20 border border-yellow-500 rounded-lg flex items-start">
            <FaExclamationTriangle className="text-yellow-400 mt-0.5 mr-2 flex-shrink-0" />
            <p className="text-yellow-200 text-sm">
              You are using a temporary password. Please change it to continue using the application.
            </p>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="mb-4 p-3 bg-green-500/20 border border-green-500 rounded-lg text-green-200 text-center">
            Password changed successfully!
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500 rounded-lg text-red-200 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Current Password */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              Current Password
            </label>
            <div className="relative">
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20 transition-all pr-10"
                placeholder="Enter current password"
                required
                data-testid="current-password-input"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              New Password
            </label>
            <div className="relative">
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20 transition-all pr-10"
                placeholder="Enter new password (min 6 characters)"
                required
                minLength={6}
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showNewPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20 transition-all"
              placeholder="Confirm new password"
              required
              data-testid="confirm-password-input"
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || success}
            className="w-full py-3 px-4 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] focus:outline-none transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            data-testid="change-password-submit-btn"
          >
            {loading ? (
              <>
                <FaSpinner className="animate-spin mr-2" />
                Changing Password...
              </>
            ) : success ? (
              'Password Changed!'
            ) : (
              'Change Password'
            )}
          </button>
        </form>

        {/* Cancel button for non-forced mode */}
        {!isForced && !success && (
          <button
            onClick={onClose}
            className="w-full mt-3 py-2 text-gray-400 hover:text-white transition-colors text-sm"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};

export default ChangePasswordModal;
